import './CreateCustomAccountGroupTable.scss';

import React from 'react';
import { connect } from 'react-redux';
import Store from 'state/Store';
import { isEqual, omit } from 'lodash';
import {
  mapCustomAccountGroupData,
  getAllAccountDescriptors
} from 'state/preferences/PreferencesSelectors';
import { tableTypes } from 'services/TableSupport';
import Optioner from 'components/shared/Optioner/Optioner';
import { updateCustomerPreferences } from 'services/Rest';
import {
  updateCustomAccountGroup,
  changeAccountSelection,
  selectAllClearAllAccounts,
  errorCustomAccountGroups
} from 'state/preferences/PreferencesActions';
import { getActiveCustomAccountGroup } from 'state/preferences/PreferencesReducer';
import TextInput from 'components/shared/Text/TextInput';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import ProfileLabeledInput from 'components/layouts/Preferences/ProfileLabeledInput';
import { isValidCustomAccountGroup } from 'services/Validation';
import { clearInvalidFields, clearTouchedFields, touchedField } from 'state/validation/ValidationActions';
import { shouldFieldValidate } from 'support/AddressUtils';


const Owners = ({ owners }) => <span>{owners}</span>;

const AccountNumber = ({ accountWChecksum }) => (
  <div className="mobile-create-custom-group-account-checksum-column">
    {' '}
    {accountWChecksum}{' '}
  </div>
);

const OwnersWithPermissions = WithUserPermissions(Owners);
const AccountNumberWithPermissions = WithUserPermissions(AccountNumber);

const isParentRow = (row) => row._type === tableTypes.HEADER_ROW;

class CreateCustomAccountGroupTable extends React.Component {
  state = {
    isGroupNameValid: true,
    initialAccountGroup: {}
  };

  componentDidMount() {
    const { activeCustomAccountGroup } = this.props;
    this.setState({initialAccountGroup: activeCustomAccountGroup})
  }

  selectAllAccounts = () => {
    const { accountDescriptors } = this.props;
    Store.dispatch(selectAllClearAllAccounts(accountDescriptors));
  };

  clearAllAccounts = () => {
    Store.dispatch(selectAllClearAllAccounts());
  };

  selectAccount = (event, details) => {
    const accountDescriptor = prepareAccountDescriptor(details);
    Store.dispatch(changeAccountSelection(accountDescriptor));
  };

  selectAccountGroup = (event, group) => {
    group.children.forEach((child) => {
      const accountDescriptor = prepareAccountDescriptor(child.details);
      Store.dispatch(changeAccountSelection(accountDescriptor, group));
    });
  };

  updateGroupName = (e) => {
      Store.dispatch(touchedField('groupName'));
      Store.dispatch(updateCustomAccountGroup('groupName', e.currentTarget.value)); 
  };

  onCancel = () => {
    Store.dispatch(clearTouchedFields())
    Store.dispatch(clearInvalidFields())
    this.props.close()
  }

  onSave = () => {
    const { activeCustomAccountGroup, close } = this.props;
    const addCustomGroupPayload = {
      customAccountGroups: [
        {
          groupName: activeCustomAccountGroup.groupName.trim(),
          groupId: activeCustomAccountGroup.groupId,
          accountDescriptors: activeCustomAccountGroup.accountDescriptors,
          disposition: {
            action: activeCustomAccountGroup.groupId === null ? 'add' : 'update'
          }
        }
      ]
    };

    updateCustomerPreferences(
      addCustomGroupPayload,
      () => {
        Store.dispatch(clearTouchedFields())
        Store.dispatch(clearInvalidFields())
        close()
      },
      () => { Store.dispatch(errorCustomAccountGroups()) }
    );
  };

  shouldDisableSave = () => {
    const { activeCustomAccountGroup } = this.props;
    const { groupName, accountDescriptors } = activeCustomAccountGroup;
    const trimmedName = groupName.trim();
    const filteredActiveCustomAccountGroup = omit(activeCustomAccountGroup, 'updated')
    return (
      activeCustomAccountGroup && (accountDescriptors.length === 0 ||
        trimmedName.length === 0 || this.props.invalidFields['groupName'] || isEqual(filteredActiveCustomAccountGroup, this.state.initialAccountGroup))
    );
  };

  shouldValidateField = (field) => {
    return shouldFieldValidate(field, this.props.touchedFields)
  };

  onFieldBlur = (field) => () => Store.dispatch(touchedField(field));

  customAccountValidator = (field) => ({
    field,
    shouldValidate: this.shouldValidateField(field),
    value: this.props.activeCustomAccountGroup[field],
    validator: (value) => isValidCustomAccountGroup(value)
  });

  render() {
    const {
      isDesktopLayout,
      activeCustomAccountGroup,
      accountGroups = [],
      isEdit,
      deleteHandler
    } = this.props;
    const groupId = activeCustomAccountGroup
      ? activeCustomAccountGroup.groupId
      : '';
    const shouldDisableSave = this.shouldDisableSave();
    return (
      <div className="create-custom-account-group">
        {isDesktopLayout && (
          <ProfileLabeledInput
            className={[!this.props.invalidFields['groupName'] ? 'invalid-group-name' : ''].join(' ')}
            errorMessage={this.props.invalidFields['groupName']}
            label={'Group Name'}
            validators={[this.customAccountValidator('groupName')]}
          >
            <div className="custom-account-group-name">
              <TextInput
                className={[this.props.invalidFields['groupName'] ? 'invalid-group-name invalid-text-input' : ''].join(' ')}
                value={activeCustomAccountGroup.groupName}
                onChange={this.updateGroupName}
                onBlur={this.onFieldBlur('groupName')}
              />
            </div>
          </ProfileLabeledInput>
        )}
        {!isDesktopLayout && (
          <div className="custom-account-group-name custom-account-group-name-mobile">
            <ProfileLabeledInput
              className={[this.props.invalidFields['groupName'] ? 'invalid-group-text' : '', 'black-text'].join(' ')}
              errorMessage={this.props.invalidFields['groupName']}
              label={'Group Name'}
              validators={[this.customAccountValidator('groupName')]}
            >
              <TextInput
                className="mobile-custom-account-grouptextBox"
                value={activeCustomAccountGroup.groupName}
                onChange={this.updateGroupName}
                onBlur={this.onFieldBlur('groupName')}
              />
            </ProfileLabeledInput>
            {isEdit && (
              <div className="custom-account-delete-container">
                <button
                  className="custom-account-delete-button"
                  onClick={() => deleteHandler(groupId)}>
                  {' '}
                  Delete{' '}
                </button>
              </div>
            )}
          </div>
        )}

        <TableHeading
          selectAllAccounts={this.selectAllAccounts}
          clearAllAccounts={this.clearAllAccounts}
        />
        <div className="create-custom-group-table-outer">
          <div className="create-custom-group-table-inner">
            {accountGroups.map((group, i) => (
              <table className="create-custom-group-table" key={i}>
                <tbody>
                  <TableRows
                    rows={group}
                    key={i}
                    selectAccount={this.selectAccount}
                    isDesktopLayout={isDesktopLayout}
                    selectAccountGroup={this.selectAccountGroup}
                  />
                </tbody>
              </table>
            ))}
          </div>
        </div>
        <div className="create-custom-group-submit-container">
          <button className="customize-accounts-cancel-button" onClick={this.onCancel}>
            Cancel
          </button>
          <SaveLoadingButtonWithPermissions
            className="customize-accounts-save-button"
            id="save-custom-account-group"
            onClick={this.onSave}
            disabled={shouldDisableSave}
            requestStatusKey="updateCustomerPreferences"
            shouldDisable={this.shouldDisableSave()}
          >
            Save
          </SaveLoadingButtonWithPermissions>
        </div>
      </div>
    );
  }
}

const prepareAccountDescriptor = (accData) => {
  const {
    suppressFromView,
    name: {
      accountId,
      accountNumber,
      accountChecksum,
      productType,
      trpCode,
      accountTypeId
    }
  } = accData;
  return {
    suppressFromView,
    accountId,
    accountNumber,
    accountChecksum,
    productType,
    trpCode,
    accountTypeId
  };
};

const TableHeading = ({ selectAllAccounts, clearAllAccounts }) => {
  return (
    <div className="create-custom-group-select-container">
      <span className="create-custom-group-select-account-text">
        {' '}
        Select Accounts{' '}
      </span>
      <div className="create-custom-group-select-account-nav">
        <span
          className="create-custom-group-select-all"
          onClick={selectAllAccounts}>
          {' '}
          Select All{' '}
        </span>
        <span onClick={clearAllAccounts}> Clear All </span>
      </div>
    </div>
  );
};

const TableRows = ({
  rows,
  parentKey = '',
  isDesktopLayout,
  selectAccountGroup,
  selectAccount
}) =>
  combineRows(
    rows,
    parentKey,
    isDesktopLayout,
    selectAccountGroup,
    selectAccount
  );

const combineRows = (
  rows = [],
  parentKey = '',
  isDesktopLayout,
  selectAccountGroup,
  selectAccount
) => {
  return [].concat(rows).reduce((acc, row, childKey) => {
    const key = parentKey + ':' + childKey;
    
    return acc
      .concat(
        <Row
          key={key}
          row={row}
          selectAccountGroup={selectAccountGroup}
          details={row.details}
          selectAccount={selectAccount}
          isParentRow={isParentRow(row)}
          isDesktopLayout={isDesktopLayout}
        />
      )
      .concat(
        row._type === tableTypes.ACCOUNT_ROW
          ? []
          : combineRows(
            row.children,
            key,
            isDesktopLayout,
            selectAccountGroup,
            selectAccount
          )
      );
  }, []);
};

const Row = ({
  row = {},
  details = {},
  isParentRow,
  isDesktopLayout,
  selectAccountGroup,
  selectAccount
}) => {
  return (
    <Optioner val={{ isParentRow }}>
      {{
        isParentRow: () => (
          <TableHeaderRow
            details={details}
            group={row}
            selectAccountGroup={selectAccountGroup}
          />
        ),
        _: () => (
          <TableBodyRows
            details={details}
            selectAccount={selectAccount}
            isDesktopLayout={isDesktopLayout}
          />
        )
      }}
    </Optioner>
  );
};

const TableHeaderRow = ({ details, group, selectAccountGroup }) => {
  const {
    selected,
    name: {
      groupingClassName,
      groupLabel,
      groupLabel: { owners, accountType }
    }
  } = details;
  return (
    <tr className={`create-custom-group-parent ${groupingClassName} header`}>
      {accountType ? (
        <td colSpan="3" className="create-custom-group-parent-header">
          <div className="checkbox-container-group-header">
            <Checkbox
              className="create-custom-group-checkbox"
              name={accountType}
              checked={selected}
              onChange={(event) => selectAccountGroup(event, group)}
            />
          </div>
          <span>{accountType}</span>
          <div className="create-custom-group-account-info">
            <OwnersWithPermissions owners={owners} shouldMask />
          </div>
        </td>
      ) : (
        <td colSpan="3" className="create-custom-group-parent-header">
          {groupLabel}
        </td>
      )}
    </tr>
  );
};

const TableBodyRows = ({ details, isDesktopLayout, selectAccount }) => {
  const {
    name: { accountWChecksum }
  } = details;
  return (
    <tr className="create-custom-group-subgroup-row">
      <td className="create-custom-group-account-details-column">
        <AccountDetails
          details={details}
          selectAccount={selectAccount}
          isDesktopLayout={isDesktopLayout}
        />
      </td>
      {isDesktopLayout && (
        <td className="create-custom-group-account-checksum-column">
          <AccountNumberWithPermissions
            accountWChecksum={accountWChecksum}
            shouldMask
          />
        </td>
      )}
    </tr>
  );
};

const AccountDetails = ({ details, isDesktopLayout, selectAccount }) => {
  const {
    selected,
    name: { accountId, accountName, ticker, accountWChecksum }
  } = details;
  return (
    <div className="create-custom-group-account-details">
      {accountName && (
        <div>
          <div className="checkbox-container-account-details">
            <Checkbox
              className="create-custom-group-checkbox"
              name={accountId}
              checked={selected}
              onChange={(event) => selectAccount(event, details)}
            />
          </div>
          <span className="create-custom-group-account-name">
            {accountName}
          </span>
          {ticker && (
            <span className="create-custom-group-account-ticker">{ticker}</span>
          )}
          <div>
            {!isDesktopLayout && (
              <AccountNumberWithPermissions
                accountWChecksum={accountWChecksum}
                shouldMask
              />
            )}
          </div>
        </div>
      )}
      {!accountName && <div>N/A</div>}
    </div>
  );
};

CreateCustomAccountGroupTable.defaultProps = {
  activeCustomAccountGroup: {
    groupId: '',
    groupName: '',
    accountDescriptors: []
  }
};

const mapStateToProps = (state) => ({
  accounts: state.accounts,
  accountDescriptors: getAllAccountDescriptors(state),
  isDesktopLayout: state.view.isDesktopLayout,
  accountGroups: mapCustomAccountGroupData(state),
  activeCustomAccountGroup: getActiveCustomAccountGroup(
    state.customerPreferences.customAccountGroups
  ),
  touchedFields: state.touchedFields,
  invalidFields: state.invalidFields,
});

export default connect(mapStateToProps)(CreateCustomAccountGroupTable);
